import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyRound, Plus } from 'lucide-react';
import { useTheme } from '../../../components/common/Theme';
import { useToast } from '../../../components/common/Toast';
import { CreateModelDialog, CreateSpaceDialog, EditModelDialog, EditSpaceDialog, type CreateModelInput } from './components/ModelDialogs';
import ConnectionAssetBrowser, { type DiscoveredModelAsset } from './components/ConnectionAssetBrowser';
import CatalogWorkspace from './components/CatalogWorkspace';
import SpacesPanel from './components/SpacesPanel';
import SpaceWorkspace, { type ModelSort, type StatusFilter } from './components/SpaceWorkspace';
import TableDetail from './components/TableDetail';
import ApiKeysDialog from './components/ApiKeysDialog';
import { ghostBtn, primaryBtn, surfaces } from './components/dataModelsUi';
import { ColumnClassificationProvider } from './columnClassificationStore';
import { GovernanceProvider } from './components/governanceStore';
import PolicyEditorPage from './governance/PolicyEditorPage';
import { parsePolicyHash, PolicyEditorNavProvider, usePolicyEditorNav, type PolicyEditorView } from './governance/policyEditorNav';
import { logDataModels } from './dataModelsLogger';
import { appendWorkspaceAssets, attachWorkspaceConnections, catalogFromWorkspaces, createWorkspace, createWorkspaceModel, deleteWorkspace, deleteWorkspaceModel, detachWorkspaceConnection, dropWorkspace, emptyCatalog, listWorkspaces, mergeWorkspace, removeWorkspaceAsset, updateWorkspace, updateWorkspaceModel } from './workspaceTreeApi';
import type { CatalogTabId, DataModel, DataModelsCatalog, DataSource, ModelTable, PublishStatus, Space, SpaceIconKey, SpaceStatus, SpaceTabId, TableDetailTabId } from './dataModelsTypes';
import { hydrateDataModelsUiState, writeDataModelsUiState } from './dataModelsUiState';
import { reorderSpaceIds, syncSpaceOrderIds, type SpaceOrderPlace } from './dataModelsUtils';

function sourcesFromDiscovered(assets: DiscoveredModelAsset[] | undefined): DataSource[] {
  const sources: DataSource[] = [];
  const seen = new Set<string>();
  (assets ?? []).forEach((asset) => {
    if (!asset.sourceId || seen.has(asset.sourceId)) return;
    seen.add(asset.sourceId);
    sources.push({
      id: asset.sourceId,
      name: asset.sourceName,
      kind: asset.sourceKind,
      environment: asset.environment,
      connectionType: asset.connectionType,
      connectorKey: asset.connectorKey,
    });
  });
  return sources;
}

function sourceIdsForSpace(catalog: DataModelsCatalog, spaceId: string): string[] {
  const space = catalog.spaces.find((item) => item.id === spaceId);
  const modelIds = new Set(catalog.models.filter((model) => model.spaceId === spaceId).map((model) => model.id));
  const ids = new Set((space?.sourceIds ?? []).filter(Boolean));
  catalog.tables.forEach((table) => {
    if (modelIds.has(table.modelId) && table.sourceId) ids.add(table.sourceId);
  });
  return Array.from(ids);
}

function WorkSpaceMark() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M12 2.4 21 7.1 12 11.8 3 7.1 12 2.4Z" />
      <path fill="currentColor" fillOpacity="0.62" d="M3 10.05 12 14.75 21 10.05v2.35L12 17.1 3 12.4v-2.35Z" />
      <path fill="currentColor" fillOpacity="0.34" d="M3 15.15 12 19.85 21 15.15v2.35L12 22.6 3 17.5v-2.35Z" />
    </svg>
  );
}

type DialogState =
  | { kind: 'closed' }
  | { kind: 'create-space' }
  | { kind: 'api-keys' }
  | { kind: 'edit-space' }
  | { kind: 'create-model' }
  | { kind: 'edit-model' }
  | { kind: 'add-table'; modelId: string };

function readPolicyHash(): PolicyEditorView | null {
  return typeof window === 'undefined' ? null : parsePolicyHash(window.location.hash);
}

export default function DataModelsPage() {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const { showToast } = useToast();
  const toast = (type: 'success' | 'error' | 'info', message: string) => showToast({ type, message });

  const [{ snapshot: initialUi, restored: uiRestored }] = useState(() => hydrateDataModelsUiState(readPolicyHash()));
  const [catalog, setCatalog] = useState<DataModelsCatalog>(() => emptyCatalog());
  const [treeLoading, setTreeLoading] = useState(true);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(initialUi.selectedSpaceId);
  const [expandedSpaceIds, setExpandedSpaceIds] = useState<string[]>(initialUi.expandedSpaceIds);
  const [expandedModelIds, setExpandedModelIds] = useState<string[]>(initialUi.expandedModelIds);
  const [spaceOrderIds, setSpaceOrderIds] = useState<string[]>(initialUi.spaceOrderIds);
  const [spaceTab, setSpaceTab] = useState<SpaceTabId>(initialUi.spaceTab);
  const [railOpen, setRailOpen] = useState(initialUi.railOpen);
  const [catalogTab, setCatalogTab] = useState<CatalogTabId>(initialUi.catalogTab);
  const [tableTab, setTableTab] = useState<TableDetailTabId>(initialUi.tableTab);
  const [treeQuery, setTreeQuery] = useState('');
  const [modelQuery, setModelQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortMode, setSortMode] = useState<ModelSort>('name');
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const [connectionBusy, setConnectionBusy] = useState(false);
  const [assetBusy, setAssetBusy] = useState(false);
  const [selectedTableId, setSelectedTableId] = useState<string | null>(initialUi.selectedTableId);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(initialUi.selectedModelId);

  const applyPolicyRoute = useCallback((view: PolicyEditorView) => {
    const spaceId = view.spaceId;
    const modelId = view.modelId;
    if (spaceId) {
      setSelectedSpaceId(spaceId);
      setExpandedSpaceIds((prev) => (prev.includes(spaceId) ? prev : [...prev, spaceId]));
    }
    if (modelId) {
      setExpandedModelIds((prev) => (prev.includes(modelId) ? prev : [...prev, modelId]));
    }
    if (view.level === 'asset') {
      setSelectedTableId(view.tableId ?? null);
      setSelectedModelId(null);
    } else if (view.level === 'model') {
      setSelectedTableId(null);
      setSelectedModelId(view.modelId ?? null);
    } else {
      setSelectedTableId(null);
      setSelectedModelId(null);
      setSpaceTab('governance');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTreeLoading(true);
    listWorkspaces()
      .then((rows) => {
        if (cancelled) return;
        const next = catalogFromWorkspaces(rows);
        setCatalog(next);
        setSelectedSpaceId((current) => {
          if (current && next.spaces.some((space) => space.id === current)) return current;
          return next.spaces[0]?.id ?? null;
        });
        setSelectedModelId((current) => (current && next.models.some((model) => model.id === current) ? current : null));
        setSelectedTableId((current) => (current && next.tables.some((table) => table.id === current) ? current : null));
        setExpandedSpaceIds((current) => {
          const known = current.filter((id) => next.spaces.some((space) => space.id === id));
          if (known.length || uiRestored) return known;
          return next.spaces[0] ? [next.spaces[0].id] : [];
        });
        setExpandedModelIds((current) => current.filter((id) => next.models.some((model) => model.id === id)));
        setSpaceOrderIds((current) => syncSpaceOrderIds(current, next.spaces.map((space) => space.id)));
        logDataModels('app', 'page_opened', {
          spaces: next.spaces.length,
          models: next.models.length,
          tables: next.tables.length,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load WorkSpaces';
        toast('error', message);
      })
      .finally(() => {
        if (!cancelled) setTreeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uiRestored]);

  useEffect(() => {
    if (treeLoading) return;
    writeDataModelsUiState({
      railOpen,
      selectedSpaceId,
      selectedModelId,
      selectedTableId,
      expandedSpaceIds,
      expandedModelIds,
      spaceOrderIds,
      spaceTab,
      catalogTab,
      tableTab,
    });
  }, [
    treeLoading,
    railOpen,
    selectedSpaceId,
    selectedModelId,
    selectedTableId,
    expandedSpaceIds,
    expandedModelIds,
    spaceOrderIds,
    spaceTab,
    catalogTab,
    tableTab,
  ]);

  const selectedSpace = catalog.spaces.find((space) => space.id === selectedSpaceId) ?? catalog.spaces[0] ?? null;

  const spaceModels = useMemo(
    () => (selectedSpace ? catalog.models.filter((model) => model.spaceId === selectedSpace.id) : []),
    [catalog.models, selectedSpace]
  );

  const treeNodes = useMemo(() => {
    const query = treeQuery.trim().toLowerCase();
    const rank = new Map(spaceOrderIds.map((id, index) => [id, index]));
    return catalog.spaces
      .map((space) => {
        const models = catalog.models
          .filter((model) => model.spaceId === space.id)
          .map((model) => ({
            model,
            tables: catalog.tables.filter((table) => table.modelId === model.id),
          }))
          .filter(({ model, tables }) => {
            if (!query) return true;
            return (
              space.name.toLowerCase().includes(query) ||
              space.description.toLowerCase().includes(query) ||
              model.name.toLowerCase().includes(query) ||
              tables.some((table) => table.name.toLowerCase().includes(query))
            );
          });
        return { space, models };
      })
      .filter(({ space, models }) => {
        if (!query) return true;
        return space.name.toLowerCase().includes(query) || space.description.toLowerCase().includes(query) || models.length > 0;
      })
      .sort((left, right) => {
        const leftRank = rank.has(left.space.id) ? rank.get(left.space.id)! : Number.MAX_SAFE_INTEGER;
        const rightRank = rank.has(right.space.id) ? rank.get(right.space.id)! : Number.MAX_SAFE_INTEGER;
        return leftRank - rightRank;
      });
  }, [catalog, spaceOrderIds, treeQuery]);

  const reorderSpaces = (draggedId: string, targetId: string, place: SpaceOrderPlace) => {
    const next = reorderSpaceIds(syncSpaceOrderIds(spaceOrderIds, catalog.spaces.map((space) => space.id)), draggedId, targetId, place);
    if (next.length === spaceOrderIds.length && next.every((id, index) => id === spaceOrderIds[index])) return;
    setSpaceOrderIds(next);
    logDataModels('app', 'workspace_order_changed', { draggedId, targetId, place });
  };

  const toggleList = (ids: string[], id: string) => (ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id]);

  const selectSpace = (spaceId: string) => {
    setSelectedSpaceId(spaceId);
    setSelectedModelId(null);
    setSelectedTableId(null);
    setSpaceTab('overview');
    setExpandedSpaceIds((prev) => (prev.includes(spaceId) ? prev : [...prev, spaceId]));
    logDataModels('app', 'space_opened', { spaceId });
  };

  const selectModel = (spaceId: string, modelId: string) => {
    setSelectedSpaceId(spaceId);
    setSelectedModelId(modelId);
    setSelectedTableId(null);
    setExpandedSpaceIds((prev) => (prev.includes(spaceId) ? prev : [...prev, spaceId]));
    logDataModels('app', 'catalog_opened', { spaceId, modelId });
  };

  const selectTable = (spaceId: string, modelId: string, tableId: string, keepCatalog = false) => {
    setSelectedSpaceId(spaceId);
    setSelectedTableId(tableId);
    if (!keepCatalog) setSelectedModelId(null);
    setExpandedSpaceIds((prev) => (prev.includes(spaceId) ? prev : [...prev, spaceId]));
    setExpandedModelIds((prev) => (prev.includes(modelId) ? prev : [...prev, modelId]));
    logDataModels('app', 'table_opened', { spaceId, modelId, tableId });
  };

  const createSpace = (input: { name: string; description: string; icon: SpaceIconKey; owner: string; tags: string[]; sourceIds: string[]; sources: DataSource[] }) => {
    void createWorkspace({
      name: input.name,
      description: input.description,
      icon: input.icon,
      owner: input.owner,
      tags: input.tags,
      sourceIds: input.sourceIds,
      sources: input.sources,
    })
      .then((saved) => {
        setCatalog((prev) => mergeWorkspace(prev, saved));
        setSelectedSpaceId(saved.id);
        setExpandedSpaceIds((prev) => (prev.includes(saved.id) ? prev : [...prev, saved.id]));
        setSpaceOrderIds((prev) => (prev.includes(saved.id) ? prev : [...prev, saved.id]));
        setSpaceTab('overview');
        setDialog({ kind: 'closed' });
        logDataModels('app', 'space_created', { spaceId: saved.id, name: saved.name });
        toast('success', `Created WorkSpace ${saved.name}`);
      })
      .catch((err: unknown) => {
        toast('error', err instanceof Error ? err.message : 'Unable to create WorkSpace');
      });
  };

  const editSpace = (input: { name: string; description: string; icon: SpaceIconKey; status: SpaceStatus }) => {
    if (!selectedSpace) return;
    const spaceId = selectedSpace.id;
    void updateWorkspace(spaceId, input)
      .then((saved) => {
        setCatalog((prev) => mergeWorkspace(prev, saved));
        setDialog({ kind: 'closed' });
        logDataModels('app', 'space_updated', { spaceId });
        toast('success', `Updated WorkSpace ${input.name}`);
      })
      .catch((err: unknown) => {
        toast('error', err instanceof Error ? err.message : 'Unable to update WorkSpace');
      });
  };

  const deleteSpace = () => {
    if (!selectedSpace) return Promise.resolve();
    const spaceId = selectedSpace.id;
    const name = selectedSpace.name;
    const catalogCount = catalog.models.filter((model) => model.spaceId === spaceId).length;
    if (catalogCount > 0) {
      logDataModels('warning', 'workspace_delete_blocked', { spaceId, catalogCount });
      toast('error', 'Only WorkSpaces without catalogs can be deleted');
      return Promise.resolve();
    }
    const remaining = catalog.spaces.filter((space) => space.id !== spaceId);
    return deleteWorkspace(spaceId)
      .then(() => {
        setCatalog((prev) => dropWorkspace(prev, spaceId));
        setSpaceOrderIds((prev) => prev.filter((id) => id !== spaceId));
        setExpandedSpaceIds((prev) => prev.filter((id) => id !== spaceId));
        setSelectedSpaceId(remaining[0]?.id ?? null);
        setSelectedModelId(null);
        setSelectedTableId(null);
        setSpaceTab('overview');
        logDataModels('app', 'workspace_deleted', { spaceId, name });
        toast('success', `Deleted WorkSpace ${name}`);
      })
      .catch((err: unknown) => {
        toast('error', err instanceof Error ? err.message : 'Unable to delete WorkSpace');
      });
  };

  const createModel = (input: CreateModelInput) => {
    if (!selectedSpace) return;
    const spaceId = selectedSpace.id;
    const tables = (input.discoveredAssets ?? []).map((asset) => ({
      name: asset.name,
      description: `${asset.assetKind} from ${asset.sourceName}`,
      rows: 0,
      columns: 0,
      sourceId: asset.sourceId,
      path: asset.path || '',
      assetKind: asset.assetKind,
      mimeType: asset.mimeType || '',
      status: input.status,
    }));
    void createWorkspaceModel(spaceId, {
      name: input.name,
      description: input.description,
      owner: selectedSpace.owner,
      status: input.status,
      tags: input.tags,
      tables,
      sources: sourcesFromDiscovered(input.discoveredAssets),
    })
      .then((saved) => {
        const created = [...(saved.models ?? [])].sort((left, right) => String(right.updatedAt).localeCompare(String(left.updatedAt)))[0];
        setCatalog((prev) => mergeWorkspace(prev, saved));
        setExpandedSpaceIds((prev) => (prev.includes(spaceId) ? prev : [...prev, spaceId]));
        if (created?.id) {
          setExpandedModelIds((prev) => (prev.includes(created.id) ? prev : [...prev, created.id]));
        }
        setSelectedModelId(null);
        setSelectedTableId(null);
        setSpaceTab('models');
        setDialog({ kind: 'closed' });
        logDataModels('app', 'model_created', {
          modelId: created?.id,
          spaceId,
          name: input.name,
          assetCount: tables.length,
        });
        toast(
          'success',
          tables.length
            ? `Created data model ${input.name} with ${tables.length} asset${tables.length === 1 ? '' : 's'}`
            : `Created data model ${input.name}`,
        );
      })
      .catch((err: unknown) => {
        toast('error', err instanceof Error ? err.message : 'Unable to create data model');
      });
  };

  const deleteModel = (modelId: string) => {
    if (!selectedSpace) return Promise.resolve();
    const spaceId = selectedSpace.id;
    const model = catalog.models.find((item) => item.id === modelId);
    const assetCount = catalog.tables.filter((table) => table.modelId === modelId).length;
    if (assetCount > 0) {
      logDataModels('warning', 'model_delete_blocked', { modelId, assetCount });
      toast('error', 'Only empty data models can be deleted');
      return Promise.resolve();
    }
    return deleteWorkspaceModel(spaceId, modelId)
      .then((saved) => {
        setCatalog((prev) => mergeWorkspace(prev, saved));
        if (selectedModelId === modelId) setSelectedModelId(null);
        setExpandedModelIds((prev) => prev.filter((id) => id !== modelId));
        logDataModels('app', 'model_deleted', { modelId, spaceId, name: model?.name });
        toast('success', `Deleted data model ${model?.name ?? ''}`.trim());
      })
      .catch((err: unknown) => {
        toast('error', err instanceof Error ? err.message : 'Unable to delete data model');
      });
  };

  const attachConnections = (incoming: DataSource[]) => {
    if (!selectedSpace) return Promise.resolve();
    const spaceId = selectedSpace.id;
    setConnectionBusy(true);
    return attachWorkspaceConnections(spaceId, incoming)
      .then((saved) => {
        setCatalog((prev) => mergeWorkspace(prev, saved));
        logDataModels('app', 'workspace_connections_attached', { spaceId, count: incoming.length });
        toast('success', incoming.length === 1 ? `Attached ${incoming[0].name}` : `Attached ${incoming.length} connections`);
      })
      .catch((err: unknown) => {
        toast('error', err instanceof Error ? err.message : 'Unable to attach connections');
      })
      .finally(() => setConnectionBusy(false));
  };

  const detachConnection = (sourceId: string) => {
    if (!selectedSpace) return Promise.resolve();
    const spaceId = selectedSpace.id;
    const name = catalog.sources.find((source) => source.id === sourceId)?.name ?? 'connection';
    setConnectionBusy(true);
    return detachWorkspaceConnection(spaceId, sourceId)
      .then((saved) => {
        setCatalog((prev) => mergeWorkspace(prev, saved));
        logDataModels('app', 'workspace_connection_removed', { spaceId, sourceId });
        toast('success', `Removed ${name} from this WorkSpace`);
      })
      .catch((err: unknown) => {
        toast('error', err instanceof Error ? err.message : 'Unable to remove connection');
      })
      .finally(() => setConnectionBusy(false));
  };

  const editModel = (input: { name: string; description: string; tags: string[]; status: PublishStatus }) => {
    if (!selectedModelId || !selectedSpace) return;
    const spaceId = selectedSpace.id;
    const modelId = selectedModelId;
    void updateWorkspaceModel(spaceId, modelId, input)
      .then((saved) => {
        setCatalog((prev) => mergeWorkspace(prev, saved));
        setDialog({ kind: 'closed' });
        logDataModels('app', 'catalog_updated', { modelId, spaceId, name: input.name });
        toast('success', `Updated data model ${input.name}`);
      })
      .catch((err: unknown) => {
        toast('error', err instanceof Error ? err.message : 'Unable to update data model');
      });
  };

  const removeAsset = (modelId: string, tableId: string) => {
    const spaceId = catalog.models.find((model) => model.id === modelId)?.spaceId;
    if (!spaceId) return Promise.resolve();
    const name = catalog.tables.find((table) => table.id === tableId)?.name ?? 'asset';
    setAssetBusy(true);
    return removeWorkspaceAsset(spaceId, modelId, tableId)
      .then((saved) => {
        setCatalog((prev) => mergeWorkspace(prev, saved));
        setSelectedTableId((current) => (current === tableId ? null : current));
        logDataModels('app', 'catalog_asset_removed', { spaceId, modelId, tableId });
        toast('success', `Removed ${name}`);
      })
      .catch((err: unknown) => {
        toast('error', err instanceof Error ? err.message : 'Unable to remove asset');
      })
      .finally(() => setAssetBusy(false));
  };

  const addDiscoveredAssets = (modelId: string, assets: DiscoveredModelAsset[]) => {
    const model = catalog.models.find((item) => item.id === modelId);
    if (!model) return;
    const existing = new Set(
      catalog.tables
        .filter((table) => table.modelId === modelId)
        .map((table) => `${table.sourceId}:${table.name.toLowerCase()}`),
    );
    const incoming = assets.filter((asset) => asset.name && !existing.has(`${asset.sourceId}:${asset.name.toLowerCase()}`));
    if (!incoming.length) {
      setDialog({ kind: 'closed' });
      toast('info', 'Those assets are already in this catalog');
      return;
    }
    setAssetBusy(true);
    void appendWorkspaceAssets(model.spaceId, modelId, {
      tables: incoming.map((asset) => ({
        name: asset.name,
        description: `${asset.assetKind} from ${asset.sourceName}`,
        rows: 0,
        columns: 0,
        sourceId: asset.sourceId,
        path: asset.path || '',
        assetKind: asset.assetKind,
        mimeType: asset.mimeType || '',
        status: model.status,
      })),
      sources: sourcesFromDiscovered(incoming),
    })
      .then((saved) => {
        setCatalog((prev) => mergeWorkspace(prev, saved));
        setExpandedSpaceIds((prev) => (prev.includes(model.spaceId) ? prev : [...prev, model.spaceId]));
        setExpandedModelIds((prev) => (prev.includes(modelId) ? prev : [...prev, modelId]));
        setDialog({ kind: 'closed' });
        logDataModels('app', 'assets_added', { modelId, count: incoming.length });
        toast('success', `Added ${incoming.length} asset${incoming.length === 1 ? '' : 's'}`);
      })
      .catch((err: unknown) => {
        toast('error', err instanceof Error ? err.message : 'Unable to add assets');
      })
      .finally(() => setAssetBusy(false));
  };

  const ui = surfaces(isLight);
  const muted = ui.muted;
  const border = ui.border;
  const pageBg = ui.page;
  const dialogModel = dialog.kind === 'add-table' ? catalog.models.find((model) => model.id === dialog.modelId) : null;
  const selectedTable = selectedTableId ? catalog.tables.find((table) => table.id === selectedTableId) ?? null : null;
  const selectedTableModel = selectedTable ? catalog.models.find((model) => model.id === selectedTable.modelId) ?? null : null;
  const selectedTableSpace =
    selectedTableModel ? catalog.spaces.find((space) => space.id === selectedTableModel.spaceId) ?? selectedSpace : selectedSpace;
  const selectedModel = selectedModelId ? catalog.models.find((model) => model.id === selectedModelId) ?? null : null;
  const selectedModelSpace = selectedModel ? catalog.spaces.find((space) => space.id === selectedModel.spaceId) ?? selectedSpace : null;
  const selectedTableSource = selectedTable ? catalog.sources.find((source) => source.id === selectedTable.sourceId) : undefined;

  return (
    <ColumnClassificationProvider>
    <GovernanceProvider spaces={catalog.spaces} models={catalog.models} tables={catalog.tables}>
    <PolicyEditorNavProvider>
    <PolicyHashSelectionSync apply={applyPolicyRoute} />
    <div className={`flex h-full min-h-0 flex-1 flex-col overflow-hidden border ${border} ${pageBg}`}>
      <header className={`flex shrink-0 items-center justify-between gap-4 border-b px-5 py-3 ${border} ${ui.header}`}>
        <div className="flex min-w-0 items-center gap-3">
          <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${isLight ? 'bg-[#F2F4F7] text-[#1C1C1C]' : 'bg-white/10 text-white'}`}>
            <WorkSpaceMark />
          </span>
          <div className="min-w-0">
            <h1 className={`text-[17px] font-semibold tracking-tight ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>Data models</h1>
            <p className={`text-xs ${muted}`}>WorkSpaces, data models, and tables in one place.</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button type="button" className={ghostBtn(isLight)} onClick={() => setDialog({ kind: 'api-keys' })}>
            <KeyRound size={14} />
            API keys
          </button>
          <button type="button" className={primaryBtn()} onClick={() => setDialog({ kind: 'create-space' })}>
            <Plus size={14} />
            Create
          </button>
        </div>
      </header>
      <div className="flex min-h-0 flex-1 overflow-hidden">
      <PolicyAwareSpacesPanel
          isLight={isLight}
          nodes={treeNodes}
          query={treeQuery}
          onQuery={setTreeQuery}
          selectedSpaceId={selectedSpace?.id ?? null}
          expandedSpaceIds={expandedSpaceIds}
          expandedModelIds={expandedModelIds}
          onSelectSpace={selectSpace}
          onToggleSpace={(spaceId) => setExpandedSpaceIds((prev) => toggleList(prev, spaceId))}
          onToggleModel={(modelId) => setExpandedModelIds((prev) => toggleList(prev, modelId))}
          onSelectTable={selectTable}
          onSelectModel={selectModel}
          onAddModel={(spaceId) => {
            selectSpace(spaceId);
            setDialog({ kind: 'create-model' });
            logDataModels('app', 'catalog_create_opened', { spaceId });
          }}
          onAddAsset={(spaceId, modelId) => {
            selectModel(spaceId, modelId);
            setDialog({ kind: 'add-table', modelId });
            logDataModels('app', 'asset_create_opened', { spaceId, modelId });
          }}
          selectedTableId={selectedTableId}
          selectedModelId={selectedModelId}
          loading={treeLoading}
          railOpen={railOpen}
          onRailOpen={setRailOpen}
          onReorderSpaces={reorderSpaces}
        />

        <PolicyStage
          isLight={isLight}
          selectedSpace={selectedSpace}
          selectedModel={selectedModel}
          selectedModelSpace={selectedModelSpace}
          selectedTable={selectedTable}
          selectedTableModel={selectedTableModel}
          selectedTableSpace={selectedTableSpace}
          selectedTableSource={selectedTableSource}
          catalog={catalog}
          spaceModels={spaceModels}
          spaceTab={spaceTab}
          onSpaceTab={setSpaceTab}
          catalogTab={catalogTab}
          onCatalogTab={setCatalogTab}
          tableTab={tableTab}
          onTableTab={setTableTab}
          modelQuery={modelQuery}
          onModelQuery={setModelQuery}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          sortMode={sortMode}
          onSortMode={setSortMode}
          onEditSpace={() => setDialog({ kind: 'edit-space' })}
          onCreateModel={() => setDialog({ kind: 'create-model' })}
          onDeleteSpace={deleteSpace}
          onDeleteModel={deleteModel}
          connectionBusy={connectionBusy}
          onAttachConnections={attachConnections}
          onDetachConnection={detachConnection}
          onEditModel={() => setDialog({ kind: 'edit-model' })}
          onAddAsset={(modelId) => setDialog({ kind: 'add-table', modelId })}
          onRemoveAsset={removeAsset}
          assetBusy={assetBusy}
          onSelectTable={selectTable}
          onSelectModel={selectModel}
          onBackTable={() => setSelectedTableId(null)}
          onBackModel={() => setSelectedModelId(null)}
        />
        {/* catalog / workspace / table views render inside PolicyStage */}
      </div>

      {dialog.kind === 'create-space' ? (
        <CreateSpaceDialog isLight={isLight} onClose={() => setDialog({ kind: 'closed' })} onSubmit={createSpace} />
      ) : null}
      {dialog.kind === 'api-keys' ? (
        <ApiKeysDialog
          isLight={isLight}
          catalogs={catalog.models.map((model) => ({
            id: model.id,
            name: model.name,
            spaceName: catalog.spaces.find((space) => space.id === model.spaceId)?.name ?? 'WorkSpace',
          }))}
          initialCatalogIds={selectedSpace ? catalog.models.filter((model) => model.spaceId === selectedSpace.id).map((model) => model.id) : []}
          onClose={() => setDialog({ kind: 'closed' })}
          onCreate={(draft) => {
            toast('success', `Issued API key for ${draft.catalogIds.length} data model${draft.catalogIds.length === 1 ? '' : 's'}`);
          }}
        />
      ) : null}
      {dialog.kind === 'edit-space' && selectedSpace ? (
        <EditSpaceDialog isLight={isLight} space={selectedSpace} onClose={() => setDialog({ kind: 'closed' })} onSubmit={editSpace} />
      ) : null}
      {dialog.kind === 'create-model' && selectedSpace ? (
        <CreateModelDialog
          isLight={isLight}
          spaceName={selectedSpace.name}
          sourceIds={sourceIdsForSpace(catalog, selectedSpace.id)}
          onClose={() => setDialog({ kind: 'closed' })}
          onSubmit={createModel}
        />
      ) : null}
      {dialog.kind === 'edit-model' && selectedModel ? (
        <EditModelDialog
          isLight={isLight}
          model={selectedModel}
          onClose={() => setDialog({ kind: 'closed' })}
          onSubmit={editModel}
        />
      ) : null}
      {dialog.kind === 'add-table' && dialogModel ? (
        <ConnectionAssetBrowser
          isLight={isLight}
          sourceIds={sourceIdsForSpace(catalog, dialogModel.spaceId)}
          selected={[]}
          onClose={() => setDialog({ kind: 'closed' })}
          onApply={(assets) => addDiscoveredAssets(dialogModel.id, assets)}
        />
      ) : null}
    </div>
    </PolicyEditorNavProvider>
    </GovernanceProvider>
    </ColumnClassificationProvider>
  );
}

function PolicyHashSelectionSync({ apply }: { apply: (view: PolicyEditorView) => void }) {
  const { view } = usePolicyEditorNav();
  useEffect(() => {
    if (!view) return;
    apply(view);
  }, [apply, view]);
  return null;
}

function PolicyAwareSpacesPanel(props: React.ComponentProps<typeof SpacesPanel>) {
  const { view, close } = usePolicyEditorNav();
  const leavePolicy = () => {
    if (!view) return;
    close();
    logDataModels('app', 'governance_policy_route_closed', { reason: 'sidebar' });
  };
  return (
    <SpacesPanel
      {...props}
      onSelectSpace={(spaceId) => {
        leavePolicy();
        props.onSelectSpace(spaceId);
      }}
      onSelectModel={(spaceId, modelId) => {
        leavePolicy();
        props.onSelectModel(spaceId, modelId);
      }}
      onSelectTable={(spaceId, modelId, tableId) => {
        leavePolicy();
        props.onSelectTable(spaceId, modelId, tableId);
      }}
      onAddModel={(spaceId) => {
        leavePolicy();
        props.onAddModel(spaceId);
      }}
      onAddAsset={(spaceId, modelId) => {
        leavePolicy();
        props.onAddAsset(spaceId, modelId);
      }}
    />
  );
}

function PolicyStage({
  isLight,
  selectedSpace,
  selectedModel,
  selectedModelSpace,
  selectedTable,
  selectedTableModel,
  selectedTableSpace,
  selectedTableSource,
  catalog,
  spaceModels,
  spaceTab,
  onSpaceTab,
  catalogTab,
  onCatalogTab,
  tableTab,
  onTableTab,
  modelQuery,
  onModelQuery,
  statusFilter,
  onStatusFilter,
  sortMode,
  onSortMode,
  onEditSpace,
  onCreateModel,
  onDeleteSpace,
  onDeleteModel,
  connectionBusy,
  onAttachConnections,
  onDetachConnection,
  onEditModel,
  onAddAsset,
  onRemoveAsset,
  assetBusy,
  onSelectTable,
  onSelectModel,
  onBackTable,
  onBackModel,
}: {
  isLight: boolean;
  selectedSpace: Space | null;
  selectedModel: DataModel | null;
  selectedModelSpace: Space | null;
  selectedTable: ModelTable | null;
  selectedTableModel: DataModel | null;
  selectedTableSpace: Space | null;
  selectedTableSource?: DataSource;
  catalog: DataModelsCatalog;
  spaceModels: DataModel[];
  spaceTab: SpaceTabId;
  onSpaceTab: (tab: SpaceTabId) => void;
  catalogTab: CatalogTabId;
  onCatalogTab: (tab: CatalogTabId) => void;
  tableTab: TableDetailTabId;
  onTableTab: (tab: TableDetailTabId) => void;
  modelQuery: string;
  onModelQuery: (query: string) => void;
  statusFilter: StatusFilter;
  onStatusFilter: (filter: StatusFilter) => void;
  sortMode: ModelSort;
  onSortMode: (mode: ModelSort) => void;
  onEditSpace: () => void;
  onCreateModel: () => void;
  onDeleteSpace: () => Promise<void>;
  onDeleteModel: (modelId: string) => Promise<void>;
  connectionBusy: boolean;
  onAttachConnections: (sources: DataSource[]) => Promise<void>;
  onDetachConnection: (sourceId: string) => Promise<void>;
  onEditModel: () => void;
  onAddAsset: (modelId: string) => void;
  onRemoveAsset: (modelId: string, tableId: string) => Promise<void>;
  assetBusy: boolean;
  onSelectTable: (spaceId: string, modelId: string, tableId: string, keepCatalog?: boolean) => void;
  onSelectModel: (spaceId: string, modelId: string) => void;
  onBackTable: () => void;
  onBackModel: () => void;
}) {
  const { view, close } = usePolicyEditorNav();
  const muted = surfaces(isLight).muted;
  const editorSpace =
    (view?.spaceId ? catalog.spaces.find((space) => space.id === view.spaceId) : undefined)
    ?? (view?.level === 'asset' ? selectedTableSpace : view?.level === 'model' ? selectedModelSpace : selectedSpace);
  const editorModel =
    (view?.modelId ? catalog.models.find((model) => model.id === view.modelId) : undefined)
    ?? (view?.level === 'asset' ? selectedTableModel ?? undefined : view?.level === 'model' ? selectedModel ?? undefined : undefined);
  const editorTable =
    (view?.tableId ? catalog.tables.find((table) => table.id === view.tableId) : undefined)
    ?? (view?.level === 'asset' ? selectedTable ?? undefined : undefined);

  if (view && editorSpace) {
    return (
      <PolicyEditorPage
        isLight={isLight}
        mode={view.mode}
        level={view.level}
        policyId={view.policyId}
        space={editorSpace}
        model={editorModel}
        table={editorTable}
        onClose={close}
      />
    );
  }

  if (selectedTable && selectedTableModel && selectedTableSpace) {
    return (
      <TableDetail
        isLight={isLight}
        space={selectedTableSpace}
        model={selectedTableModel}
        table={selectedTable}
        source={selectedTableSource}
        onBack={onBackTable}
        tab={tableTab}
        onTab={onTableTab}
      />
    );
  }

  if (selectedModel && selectedModelSpace) {
    return (
      <CatalogWorkspace
        isLight={isLight}
        space={selectedModelSpace}
        model={selectedModel}
        tables={catalog.tables.filter((table) => table.modelId === selectedModel.id)}
        sources={catalog.sources}
        members={catalog.members.filter((member) => member.spaceId === selectedModel.spaceId)}
        onBackToSpace={onBackModel}
        onEdit={onEditModel}
        onAddAsset={() => onAddAsset(selectedModel.id)}
        onRemoveAsset={(tableId) => onRemoveAsset(selectedModel.id, tableId)}
        assetBusy={assetBusy}
        onSelectTable={(tableId) => onSelectTable(selectedModel.spaceId, selectedModel.id, tableId, true)}
        tab={catalogTab}
        onTab={onCatalogTab}
      />
    );
  }

  if (selectedSpace) {
    return (
      <SpaceWorkspace
        isLight={isLight}
        space={selectedSpace}
        models={spaceModels}
        tables={catalog.tables.filter((table) => spaceModels.some((model) => model.id === table.modelId))}
        sources={catalog.sources}
        tools={catalog.mcpTools.filter((tool) => spaceModels.some((model) => model.id === tool.modelId))}
        spaceTab={spaceTab}
        onSpaceTab={onSpaceTab}
        modelQuery={modelQuery}
        onModelQuery={onModelQuery}
        statusFilter={statusFilter}
        onStatusFilter={onStatusFilter}
        sortMode={sortMode}
        onSortMode={onSortMode}
        onEditSpace={onEditSpace}
        onCreateModel={onCreateModel}
        onDeleteSpace={onDeleteSpace}
        onDeleteModel={onDeleteModel}
        connectionBusy={connectionBusy}
        onAttachConnections={onAttachConnections}
        onDetachConnection={onDetachConnection}
        onSelectTable={(tableId) => {
          const table = catalog.tables.find((item) => item.id === tableId);
          if (!table) return;
          const model = catalog.models.find((item) => item.id === table.modelId);
          if (!model) return;
          onSelectTable(model.spaceId, model.id, table.id);
        }}
        onSelectModel={(modelId) => onSelectModel(selectedSpace.id, modelId)}
      />
    );
  }

  return <div className={`flex flex-1 items-center justify-center text-sm ${muted}`}>Create a WorkSpace to get started.</div>;
}
